import { cloneDeep } from 'lodash';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, Container, FormControl, FormSelect, Modal, Row } from 'react-bootstrap';
import { LocalizeContext } from 'react-locale-language';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getFormattedDate } from '../../../../form-generator/helpers/utility';
import { deleteFileFromServer, makeApiRequests } from '../../../../helpers/api';
import {
  ALL_ROLES,
  APP_URL,
  ENDPOINTS,
  FINANCIAL_STRATEGIST_ROLE,
  REAL_ESTATE_ANALYST_ROLE,
  SALES_ROLE,
  STATUS_FREE_IRR_CALC,
  STATUS_INTEREST_IN_MY_COURSE,
  fieldsWithFormulas,
  getCustomerSortNameToField,
  getPresetTableHeaders,
  monthSmall,
  presetTableHeaders
} from '../../../../helpers/constants';
import {
  checkIfUserCanEdit,
  evalFormula,
  generateAlphanumericString,
  isAdmin,
  validateEmail
} from '../../../../helpers/global';
import useAuth from '../../../../hooks/useAuth';
import { useScreenWidth } from '../../../../hooks/useScreenWidth';
import useApartmentProfile from '../../../../hooks/useApartmentProfile';
import AlertModal from '../../../common/AlertModal';
import FileViewer from '../../../common/FileViewer';
import HorizontalProgress from '../../../common/HorizontalProgress';
import NotFound from '../../../common/NotFound';
import SlidingSideBar from '../../../common/SlidingSideBar/SlidingSideBar';
import CompTable from '../../comp-table';
import AddEditCustomerModal from './AddEditCustomerModal';
import AddEditCustomerProfileModal from './AddEditCustomerProfileModal';
import CustomerList from './CustomerList';
import CustomerShareDialog from './CustomerShareDialog';
import InviteCustomerModal from './InviteCustomerModal';
import ReapplyCompModal from './ReapplyCompModal';
import ViewAppointmentModal from './ViewAppointmentModal';
import UserLogsModal from './strategies/UserLogsModal';
import ComparisonChart from './strategies/charts/ComparisonChart';
import {
  addMonthAndYear,
  areMonthYearEqual,
  createDefaultStrategies,
  findDiffInNumberOfMonths,
  getUpdatedLinkedVariablesOfLiability,
  updateNumberPercentage
} from './strategies/helper';
import Loader from '../../../common/Loader';
import NewCustomerCard from './NewCustomerCard';
import { useParentCustomer } from '../../../../context/ParentCustomerContext';
import DialogModal from '../../../common/DialogModal/DialogModal';
import CustomerClientInfoDialog from './CustomerClientInfoDialog';

const fieldsToExcludeForClone = [
  '_id',
  'crmContact',
  'name',
  'email',
  'secondaryName',
  'secondaryEmail',
  'address',
  'phone',
  'secondaryPhone'
];

const getCustomerForClone = customerObj => {
  let obj = cloneDeep(customerObj);
  fieldsToExcludeForClone.forEach(h => {
    delete obj[h];
  });
  return obj;
};
const applyPresetToAsset = (presetName, compTable, asset, selectedFields = []) => {
  if (!presetName) return;

  const preset = compTable?.presets?.find(p => p.name === presetName);
  if (!preset) return;

  presetTableHeaders.forEach(({ key, mapsToElem, isPercentageField, numberField, onlyForLoan }) => {
    if (
      !selectedFields.includes(key) ||
      onlyForLoan ||
      !preset[key] ||
      !mapsToElem ||
      !asset.hasOwnProperty(isPercentageField && numberField ? numberField : mapsToElem)
    )
      return;

    try {
      if (!fieldsWithFormulas.includes(key)) {
        asset[mapsToElem] = preset[key];
        return;
      }

      const assetValue = asset.value;
      const presetValue = evalFormula(preset[key], compTable.variables) || 0;

      if (isPercentageField && numberField) {
        asset[numberField] =
          key === 'expectedCashflow' ? (assetValue * presetValue) / 1200 : (assetValue * presetValue) / 100;
      } else {
        asset[mapsToElem] = presetValue;
      }
    } catch (e) {
      console.log(e);
    }
  });
  //TODO also recalculate tax
};

const applyPresetToLiability = (presetName, compTable, liability, assetForLiability, selectedFields = []) => {
  if (!presetName) return;

  const preset = compTable?.presets?.find(p => p.name === presetName);
  if (!preset) return;

  presetTableHeaders.forEach(({ key, mapsToElem, isPercentageField, numberField }) => {
    if (!selectedFields.includes(key)) return;

    try {
      if (
        !preset[key] ||
        !mapsToElem ||
        !liability.hasOwnProperty(isPercentageField && numberField ? numberField : mapsToElem)
      )
        return;

      if (!fieldsWithFormulas.includes(key)) {
        liability[mapsToElem] = preset[key];
        return;
      }

      const assetValue = assetForLiability.value;
      const liabilityValue = liability.value;
      const presetValue = evalFormula(preset[key], compTable.variables) || 0;

      if (isPercentageField && numberField) {
        if (key === 'loanValue') {
          liability[numberField] = (assetValue * presetValue) / 100;
        } else {
          liability[numberField] = (liabilityValue * presetValue) / 100;
        }
      } else {
        liability[mapsToElem] = presetValue;
      }
    } catch (e) {
      console.log(e);
    }
  });
};

const UpdateStrategyTimeModal = ({
  show,
  onHide,
  showProgress,
  translate,
  strategyStartMonth,
  strategyStartYear,
  onStartMonthChange,
  onStartYearChange,
  strategyTotalYears,
  onTotalYearsChange,
  onStrategyStartDateUpdateSubmit
}) => {
  return (
    <Modal size="lg" show={show} onHide={onHide} centered backdrop="static">
      <Modal.Header closeButton={!showProgress}>
        <Modal.Title className="flex flex-1">
          <h6 className="mb-0 d-inline-block">{translate('update_strategy_start_time')}</h6>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="overflow-auto">
        <div className="mt-1 p-2">
          <Row>
            <Col xs={4}>
              <h6 className="mid">
                {translate('start_month')}
                <span className="text-danger">*</span>
              </h6>
              <FormSelect
                size="sm"
                id="startMonth"
                onChange={onStartMonthChange}
                value={strategyStartMonth}
                name="startMonth"
              >
                {monthSmall.map((month, index) => {
                  return (
                    <option value={index} key={index}>
                      {month}
                    </option>
                  );
                })}
              </FormSelect>
            </Col>
            <Col xs={4}>
              <h6 className="mid">
                {translate('start_year')}
                <span className="text-danger">*</span>
              </h6>
              <FormControl
                size="sm"
                type="number"
                id="startYear"
                onChange={onStartYearChange}
                value={strategyStartYear}
                name="startYear"
              />
            </Col>
            <Col xs={4}>
              <h6 className="mid">
                {translate('total_years')}
                <span className="text-danger">*</span>
              </h6>
              <FormControl
                size="sm"
                type="number"
                id="totalYears"
                onChange={onTotalYearsChange}
                value={strategyTotalYears}
                name="totalYears"
              />
            </Col>
          </Row>
          <h6 className="smallFont mt-2 text-muted">{translate('update_start_date_note_for_strategies')}</h6>
          <Row className="mt-4">
            <Col xs={12} className={'mt-2 mt-md-0'}>
              <Button size="sm" onClick={onStrategyStartDateUpdateSubmit} disabled={showProgress}>
                {translate('update')}
              </Button>
            </Col>
          </Row>
        </div>

        {showProgress && <HorizontalProgress text={`${translate('updating_strategy_time')}...`} />}
      </Modal.Body>
    </Modal>
  );
};

const CompTableSidebar = ({ customer, show, onHide, onCompUpdate, translate }) => (
  <div>
    <DialogModal
      visible={show}
      onClose={onHide}
      title={`${translate('comp_table')} (${customer?.name || translate('no_name')})`}
    >
      {show && <CompTable compId={customer.comp._id} onCompUpdate={onCompUpdate} />}
    </DialogModal>
  </div>
);

const ComparisonChartSidebar = ({ customer, activeStrategy, show, onHide, translate, adminCompTable }) => (
  <DialogModal
    visible={show}
    onClose={onHide}
    title={`${translate('compare_strategies')} (${customer?.name || translate('no_name')})`}
  >
    {show && (
      <ComparisonChart
        customer={customer}
        activeStrategy={activeStrategy}
        strategies={customer.strategies}
        customerCompTable={customer['comp']}
        adminCompTable={adminCompTable}
      />
    )}
  </DialogModal>
);

const Customer = ({ assigneUsers, compTable: adminCompTable }) => {
  const { user } = useAuth();
  const { search } = useLocation();
  const { screenWidth } = useScreenWidth();
  const searchParams = new URLSearchParams(search);
  const customerEmailsFromUrl = searchParams.get('customerEmails');
  const customerIdFromUrl = searchParams.get('customerToView');

  const role = useMemo(() => user.role, [user]);
  const readOnlyMode = useMemo(() => !checkIfUserCanEdit(user), [user]);

  const { customers, setCustomers } = useParentCustomer();
  const [selectedStatus, setSelectedStatus] = useState('All');
  const [showAlertMeta, setShowAlertMeta] = useState(null);
  const [customerActionMeta, setCustomerActionMeta] = useState(null);
  const [updateStrategyTimeMeta, setUpdateStrategyTimeMeta] = useState(null);
  const [updatingStrategyTime, setUpdatingStrategyTime] = useState(false);
  const [customerAddingUpdating, setCustomerAddingUpdating] = useState(false);
  const [viewAppointmentModalMeta, setViewAppointmentModalMeta] = useState(null);
  //? this is the active customer selected from list
  // const [parentCustomer, setParentCustomer] = useState();

  const { parentCustomer, setParentCustomer } = useParentCustomer();

  //? a parent customer can also have multiple child customer, a customer is selected from one of these
  //? this is the currently active customer from parent customer [this can also be the parent customer itself]
  const [activeCustomer, setActiveCustomer] = useState();
  const [initialCustomers, setInitialCustomers] = useState([]);
  //collapsed for smaller screens
  const [isSidebarCollapsed, setSidebarCollapsed] = useState(() => window.innerWidth < 768);
  const [showCompTable, setShowCompTable] = useState(false);
  const [showCustomerGeneralInfo, setShowCustomerGeneralInfo] = useState(false);
  const [showCustomerClientInfo, setShowCustomerClientInfo] = useState(false);

  const [showComparisonChart, setShowComparisonChart] = useState(false);

  const [activeCustomerStrategy, setActiveCustomerStrategy] = useState();
  const [reapplyCompModalMeta, setReapplyCompModalMeta] = useState(null);
  const [shareDialogMeta, setShareDialogMeta] = useState(null);

  const { translate, langCode } = useContext(LocalizeContext);

  const sortNameToField = useMemo(() => getCustomerSortNameToField(translate), [langCode]);

  const [showFiles, setShowFiles] = useState(false);

  const [isFileUploadingToCustomer, setFileUploadingToCustomer] = useState(false);
  const [deleteFileModalMeta, setDeleteFileModalMeta] = useState(null);
  const [inviteCustomerModalMeta, setInviteCustomerModalMeta] = useState(null);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [customerUpdating, setCustomerUpdating] = useState(false);

  const {
    customerProfileMeta,
    deleteApartmentProfileMeta,
    handleAddProfileClick,
    handleDeleteProfileClick,
    submitCustomerProfile,
    deleteApartmentProfile,
    toggleProfileApproval,
    publishProfileForReview,
    closeProfileModal,
    closeDeleteModal
  } = useApartmentProfile();

  const [lastRequestQuery, setLastRequestQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [userList, setUserList] = useState([]);
  // user selected from the list (to see customer track for this user)
  const [selectedUsers, setSelectedUsers] = useState(null);
  const [deletedCustomers, setDeletedCustomers] = useState([]);
  const [deletedCustomersLoading, setDeletedCustomersLoading] = useState(false);
  const [customersModifiedByUser, setCustomersModifiedByUser] = useState([]);
  const [customersModifiedByUserLoading, setCustomersModifiedByUserLoading] = useState(false);

  const fetchUsers = async () => {
    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.USERS_LIST,
      requestBody: { filter: { role: ALL_ROLES }, compactMode: true }
    });
    if (error) {
      toast.error(error);
      return;
    }
    setUserList(response.map(user => ({ _id: user._id, name: user.name })));
  };

  const fetchDeletedCustomers = async userId => {
    setDeletedCustomersLoading(true);
    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.DELETEDUSERS_LIST,
      requestBody: {
        filter: {
          role: 'Customer',
          deletedBy: userId,
          createdBy: userId,
          modifiedBy: userId
        },
        compactMode: true
      }
    });
    setDeletedCustomersLoading(false);
    if (error) {
      toast.error(error);
      return;
    }
    // flaging for creator, modifier and deleter for deleted customers
    const processedDeletedCustomers = response.map(c => {
      let customer = { ...c };
      if (c.createdBy === userId) {
        customer.isCreator = true;
      }
      if (c.modifiers?.length > 0 && c.modifiers.some(modifierId => modifierId === userId)) {
        customer.isModifier = true;
      }
      if (c.deletedBy?._id.toString() === userId.toString()) {
        customer.isDeleter = true;
      }
      return customer;
    });
    setDeletedCustomers(processedDeletedCustomers);
  };
  const fetchCustomersModifiedByUser = async userId => {
    setCustomersModifiedByUserLoading(true);
    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.USERS_LIST,
      requestBody: {
        filter: {
          role: 'Customer'
        },
        modifiedByUserId: userId,
        compactMode: true
      }
    });
    setCustomersModifiedByUserLoading(false);
    if (error) {
      toast.error(error);
      return;
    }
    const processedCustomersModifiedByUser = response.map(c => {
      let customer = { ...c, isModifier: true };
      if (c.createdBy === userId) {
        customer.isCreator = true;
      }
      return customer;
    });
    setCustomersModifiedByUser(processedCustomersModifiedByUser);
  };

  const fetchCustomers = async () => {
    setLoading(true);
    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.USERS_LIST,
      requestBody: {
        filter: {
          role: 'Customer'
        },
        compactMode: true
      }
    });
    setLoading(false);

    if (error) {
      toast.error(error);
      return;
    }

    let customersList = response.map(s => ({
      ...s,
      startDate: s.startDate ?? s.createdAt,
      customerPanel: s['active'] ? `${APP_URL}/customer/${s['_id']}` : undefined
    }));
    // setLastRequestQuery(query);
    setCustomers(customersList);
    setInitialCustomers(customersList);
  };

  useEffect(() => {
    fetchCustomers();
    fetchUsers();
  }, []);

  const handleUserFilterChange = user => {
    if (!user?._id) {
      setDeletedCustomers([]);
      setCustomersModifiedByUser([]);
      setSelectedUsers(null);
      return;
    }
    setSelectedUsers(user);
    fetchDeletedCustomers(user._id);
    fetchCustomersModifiedByUser(user._id);
  };

  // customers displayed in the list
  const displayedCustomers = useMemo(() => {
    if (!selectedUsers?._id) return customers;
    if (deletedCustomersLoading || customersModifiedByUserLoading) return [];

    const selectedUserId = selectedUsers._id;

    const modifiedCustomerIds = new Set(customersModifiedByUser.map(c => c._id));
    // Keep only active customers created or modified by the selected user; tag each with isCreator / isModifier.
    const modifiedOrCreatedCustomersBySelectedUser = customers.reduce((filteredActive, customer) => {
      const isCreatedBySelectedUser = customer.createdBy === selectedUserId;
      const isModifiedBySelectedUser = modifiedCustomerIds.has(customer._id);

      if (isCreatedBySelectedUser || isModifiedBySelectedUser) {
        filteredActive.push({
          ...customer,
          isCreator: isCreatedBySelectedUser,
          isModifier: isModifiedBySelectedUser
        });
      }
      return filteredActive;
    }, []);

    return [...modifiedOrCreatedCustomersBySelectedUser, ...deletedCustomers].sort(
      (a, b) => new Date(b.updatedAt || 0).getTime() - new Date(a.updatedAt || 0).getTime()
    );
  }, [
    customers,
    deletedCustomers,
    selectedUsers?._id,
    deletedCustomersLoading,
    customersModifiedByUser,
    customersModifiedByUserLoading
  ]);

  useEffect(() => {
    let newActiveCustomerIndex = -1;

    // Check if there's a parentCustomer and find its index in the customers array
    if (parentCustomer) {
      newActiveCustomerIndex = customers.findIndex(c => c['_id'] === parentCustomer['_id']);
    }

    // If there is a last request query or the parent customer was not found
    // we want to select the contact whose name matches exactly the search query
    // or default to first one in the status
    if (lastRequestQuery || newActiveCustomerIndex === -1) {
      // Determine the active customer index based on the selected status
      newActiveCustomerIndex =
        selectedStatus === 'All'
          ? customers.findIndex(
              c =>
                ![STATUS_INTEREST_IN_MY_COURSE, STATUS_FREE_IRR_CALC].includes(c.status) &&
                (!lastRequestQuery || c.name === lastRequestQuery) &&
                !c.archived
            )
          : -1;
    }

    // Get the customer object based on the determined index or default to the first customer
    const customer = customers[newActiveCustomerIndex !== -1 ? newActiveCustomerIndex : 0];

    // Update the parentCustomer state with the selected customer or set it to undefined
    setParentCustomer(customer ? { ...customer } : undefined);

    // Clear the last request query
    setLastRequestQuery('');
  }, [customers, selectedStatus, lastRequestQuery]);

  useEffect(() => {
    if (!parentCustomer) {
      setActiveCustomer();
      return;
    }

    if (!activeCustomer) {
      setActiveCustomer(parentCustomer);
      return;
    }

    const activeInAccessible = parentCustomer.accessibleUsers?.find(c => c['_id'] === activeCustomer['_id']);

    setActiveCustomer(activeInAccessible || parentCustomer);
  }, [parentCustomer]);

  const onActiveCustomerSwitched = customer => {
    setActiveCustomer(customer);
  };

  const onCustomerUpdate = newCustomer => {
    if (!newCustomer.email) {
      newCustomer.email = '';
    }

    // update delegations and accessibles
    customers.forEach((c, index) => {
      if (c._id === newCustomer._id) {
        customers[index] = { ...customers[index], ...newCustomer };
      }

      ['accessibleUsers', 'delegations'].forEach(key => {
        if (!customers[index][key]) return;

        customers[index][key] = customers[index][key].map(oc => {
          return oc._id === newCustomer._id ? newCustomer : oc;
        });
      });
    });

    setCustomers([...customers]);
  };

  const onCustomerDelete = deletedCustomer => {
    customers.splice(
      customers.findIndex(a => a['_id'] === deletedCustomer['_id']),
      1
    );

    // update delegations and accessibles
    customers.forEach((c, index) => {
      ['accessibleUsers', 'delegations'].forEach(key => {
        if (customers[index][key])
          customers[index][key] = customers[index][key].filter(oc => oc._id !== deletedCustomer._id);
      });
    });

    setCustomers([...customers]);
  };

  const onSendInvitationLinkToCustomerClick = () => {
    setInviteCustomerModalMeta({
      customer: parentCustomer,
      receiverEmails: [parentCustomer.email, parentCustomer.secondaryEmail].filter(e => Boolean(e?.trim())),
      headingLabel: 'send_invitation_link',
      isInvitationLink: true
    });
  };

  const onInviteCustomerClick = () => {
    setInviteCustomerModalMeta({
      hasAlreadyBeenInvited: parentCustomer.userId,
      customer: parentCustomer,
      password: generateAlphanumericString(8),
      receiverEmails: [parentCustomer.email, parentCustomer.secondaryEmail].filter(e => Boolean(e?.trim())),
      headingLabel: 'Send_Password_Reset_Link',
      isInvitationLink: false
    });
  };

  const onNewCustomerFormSubmit = async form => {
    const {
      name,
      boardName,
      email,
      phone,
      secondaryName,
      secondaryEmail,
      secondaryPhone,
      address,
      status,
      allowedCalculators,
      additionalInfo,
      startDate,
      endDate,
      programType,
      level,
      levelTo,
      showGeneralInfo,
      isPublic,
      assignedTo,
      crmContact,
      showRujumBoard,
      sendEmail
    } = form;

    const { toBeClonedCustomer } = customerActionMeta;

    if (!name?.trim() && !email?.trim()) {
      return toast.error('Either name or email is required!');
    }

    setCustomerAddingUpdating(true);

    const newCustomer = {
      boardName,
      name,
      email,
      phone,
      role: 'Customer',
      address,
      status,
      allowedCalculators,
      strategies: toBeClonedCustomer ? [] : createDefaultStrategies(),
      additionalInfo,
      startDate,
      endDate,
      programType,
      customerSurvey: level > 1 ? adminCompTable?.customerSurvey : null,
      secondaryName,
      secondaryEmail,
      secondaryPhone,
      cloneFrom: toBeClonedCustomer?._id,
      level,
      levelTo,
      showGeneralInfo,
      isPublic,
      crmContact,
      showRujumBoard,
      sendEmail,
      ...(isAdmin(user?.role) ? { assignedTo } : {})
    };

    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.USERS_BASE,
      requestBody: newCustomer
    });

    setCustomerAddingUpdating(false);
    if (error) {
      toast.error(error);
      return;
    }

    setCustomers([response, ...customers]);
    setParentCustomer(response);
    setCustomerActionMeta(null);
  };

  const onUpdateCustomerFormSubmit = async form => {
    const {
      boardName,
      name,
      email,
      phone,
      address,
      status,
      allowedCalculators,
      additionalInfo,
      startDate,
      endDate,
      programType,
      secondaryName,
      secondaryEmail,
      secondaryPhone,
      level,
      levelTo,
      showGeneralInfo,
      isPublic,
      assignedTo,
      crmContact,
      showRujumBoard
    } = form;

    if (!name?.trim() && !email?.trim()) {
      return toast.error('Either name or email is required!');
    }

    setCustomerAddingUpdating(true);

    const updatedCustomer = {
      boardName,
      name,
      phone,
      email,
      address,
      status,
      allowedCalculators,
      additionalInfo,
      startDate,
      endDate,
      programType,
      secondaryName,
      secondaryEmail,
      secondaryPhone,
      level,
      levelTo,
      showGeneralInfo,
      isPublic,
      crmContact,
      showRujumBoard,
      ...(isAdmin(user?.role) ? { assignedTo } : {})
    };

    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.USERS_UPDATE,
      requestBody: { ...updatedCustomer, _id: customerActionMeta.customer._id }
    });

    setCustomerAddingUpdating(false);
    if (error) {
      toast.error(error);
      return;
    }

    onCustomerUpdate(response);
    setCustomerActionMeta(null);
  };

  const onCustomerDetailChange = customer => {
    setCustomerActionMeta({ ...customerActionMeta, customer });
  };

  const onCustomerSubmit = () => {
    const { mode, customer } = customerActionMeta;
    if (mode === 'edit') {
      onUpdateCustomerFormSubmit(customer);
    } else {
      onNewCustomerFormSubmit(customer);
    }
  };

  const deleteCustomer = async () => {
    setShowAlertMeta({ ...showAlertMeta, showProgress: true });

    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.USERS_BASE,
      method: 'DELETE',
      requestBody: { _id: parentCustomer._id }
    });

    setShowAlertMeta({ ...showAlertMeta, showProgress: false });

    if (error) {
      toast.error(error);
      return;
    }

    onCustomerDelete(parentCustomer);

    setShowAlertMeta(null);
    toast.success('Customer deleted successfully');
  };

  const archiveOrUnarchiveCustomer = async () => {
    setShowAlertMeta({ ...showAlertMeta, showProgress: true });

    const isArchived = Boolean(parentCustomer?.['archived']);
    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.USERS_UPDATE,
      requestBody: { archived: !isArchived, _id: parentCustomer._id }
    });

    setShowAlertMeta({ ...showAlertMeta, showProgress: false });

    if (error) {
      toast.error(error);
      return;
    }

    onCustomerUpdate(response);
    setShowAlertMeta(null);
    toast.success(`Customer ${isArchived ? 'unarchived' : 'archived'} successfully`);
  };

  const onDeleteCustomerClick = () => {
    setShowAlertMeta({
      progressText: `${translate('deleting_customer')}...`,
      onContinueClick: deleteCustomer,
      alertText: translate('are_you_sure_you_want_to_delete_customer', {
        userName: parentCustomer?.['name'] || 'No name'
      })
    });
  };

  const onArchiveCustomerClick = () => {
    setShowAlertMeta({
      progressText: `${translate(parentCustomer?.['archived'] ? 'unarchiving_customer' : 'archiving_customer')}...`,
      onContinueClick: archiveOrUnarchiveCustomer,
      alertText: translate(
        parentCustomer?.['archived']
          ? 'are_you_sure_you_want_to_unarchive_customer'
          : 'are_you_sure_you_want_to_archive_customer',
        {
          userName: parentCustomer?.['name'] || 'No name'
        }
      )
    });
  };

  const onCompareStrategyClick = () => {
    setShowComparisonChart(true);
  };

  const onAccountBalanceChange = e => {
    updateNumberPercentage('value', 'returnCashflow', 'returnCashflowPercentage', 'value');
  };
  const onReturnCashflowChange = e => {
    updateNumberPercentage('value', 'returnCashflow', 'returnCashflowPercentage', 'number');
  };
  const onReturnCashflowPercentageChange = e => {
    updateNumberPercentage('value', 'returnCashflow', 'returnCashflowPercentage', '%');
  };

  const alterExpandCollapse = () => {
    setSidebarCollapsed(prev => !prev);
  };

  window['onAccountBalanceChange'] = onAccountBalanceChange;
  window['onReturnCashflowChange'] = onReturnCashflowChange;
  window['onReturnCashflowPercentageChange'] = onReturnCashflowPercentageChange;

  const onCompUpdate = comp => {
    const activeCustomerTemp = customers.find(c => c._id === activeCustomer._id);
    activeCustomerTemp.comp = comp;

    onCustomerUpdate(activeCustomerTemp);
  };

  const onReapplyCompClick = () => {
    if (activeCustomer.strategies.length === 0) {
      return toast.error(translate('no_strategies_created'));
    }

    const fields = getPresetTableHeaders(translate).filter(h => h.mapsToElem);
    setReapplyCompModalMeta({
      strategies: activeCustomer.strategies,
      presetFields: fields,
      selectedFields: [],
      selectedStrategies: []
    });
  };

  const onShareClick = () => {
    setShareDialogMeta({ userBeingAdded: '' });
  };

  const onShareUserAddClick = async () => {
    const { userBeingAdded } = shareDialogMeta;

    if (!validateEmail(userBeingAdded)) {
      return toast.error('Please provide a valid email');
    }

    toast.info('Adding user...');
    setShareDialogMeta({ ...shareDialogMeta, showProgress: true });

    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.SHARE_USER_ADD,
      requestBody: {
        email: userBeingAdded,
        customerId: parentCustomer['_id']
      }
    });

    setShareDialogMeta({ ...shareDialogMeta, showProgress: false });

    if (error) {
      return toast.error(error);
    }

    setShareDialogMeta({ ...shareDialogMeta, userBeingAdded: '' });

    onCustomerUpdate(response);

    toast.success('Customer Shared succesfully');
  };

  const onShareUserRemoveClick = async user => {
    toast.info('Removing user...');
    setShareDialogMeta({ ...shareDialogMeta, showProgress: true });

    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.SHARE_USER_SUBTRACT,
      requestBody: {
        email: user.email,
        customerId: parentCustomer['_id']
      }
    });

    setShareDialogMeta({ ...shareDialogMeta, showProgress: false });

    if (error) {
      return toast.error(error);
    }

    onCustomerUpdate(response);

    toast.success('User removed successfully');
  };

  const onReapplyCompStrategyCheckChange = value => {
    const { selectedStrategies = [] } = reapplyCompModalMeta;

    if (selectedStrategies.includes(value)) {
      selectedStrategies.splice(selectedStrategies.indexOf(value), 1);
    } else {
      selectedStrategies.push(value);
    }

    setReapplyCompModalMeta({ ...reapplyCompModalMeta });
  };

  const onReapplyCompFieldCheckChange = value => {
    const { selectedFields = [] } = reapplyCompModalMeta;

    if (selectedFields.includes(value)) {
      selectedFields.splice(selectedFields.indexOf(value), 1);
    } else {
      selectedFields.push(value);
    }

    setReapplyCompModalMeta({ ...reapplyCompModalMeta });
  };

  const onStrategyAllCheck = () => {
    const { selectedStrategies = [], strategies } = reapplyCompModalMeta;

    setReapplyCompModalMeta({
      ...reapplyCompModalMeta,
      selectedStrategies: strategies.every(o => selectedStrategies.includes(o._id))
        ? []
        : reapplyCompModalMeta.strategies.map(s => s._id)
    });
  };

  const onFieldsAllCheck = () => {
    const { selectedFields = [], presetFields } = reapplyCompModalMeta;

    setReapplyCompModalMeta({
      ...reapplyCompModalMeta,
      selectedFields: presetFields.every(o => selectedFields.includes(o.key))
        ? []
        : reapplyCompModalMeta.presetFields.map(s => s.key)
    });
  };

  const reapplyComp = async () => {
    const { selectedFields = [], selectedStrategies = [] } = reapplyCompModalMeta;
    if (selectedStrategies.length === 0) {
      return toast.error('Please select atleast one strategy');
    }

    if (selectedFields.length === 0) {
      return toast.error('Please select atleast one field to reapply');
    }

    setReapplyCompModalMeta({ ...reapplyCompModalMeta, showProgress: true });

    const comp = activeCustomer.comp;

    const newStrategies = activeCustomer.strategies.map(strategy => {
      if (!selectedStrategies.includes(strategy._id)) {
        return strategy;
      }

      const clonedStrategy = cloneDeep(strategy);

      const { assets = [], liabilities = [] } = clonedStrategy;

      assets.forEach(asset => {
        applyPresetToAsset(asset.preset, comp, asset, selectedFields);
      });

      liabilities.forEach(liability => {
        const linkedAssetName = liability.relatedAssets ? liability.relatedAssets[0] : null;
        if (!linkedAssetName) return;

        const linkedAsset = assets.find(a => a.name === linkedAssetName);
        if (!linkedAsset) return;

        applyPresetToLiability(linkedAsset.preset, comp, liability, linkedAsset, selectedFields);
      });

      return clonedStrategy;
    });

    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.USERS_UPDATE,
      requestBody: {
        strategies: newStrategies,
        _id: activeCustomer['_id']
      }
    });

    setReapplyCompModalMeta({ ...reapplyCompModalMeta, showProgress: false });

    if (error) {
      return toast.error(error);
    }

    onCustomerUpdate(response);
    setReapplyCompModalMeta(null);
    toast.success(`Successfully reapplied presets!`);
  };

  const onStrategyStartDateUpdateSubmit = async () => {
    const { strategyStartYear, strategyStartMonth, strategyTotalYears } = updateStrategyTimeMeta;

    if (!strategyStartYear) {
      return toast.error('Please provide a valid year!');
    }

    const yearInNumber = Number(strategyStartYear);
    const monthInNumber = Number(strategyStartMonth);

    if (isNaN(yearInNumber) || strategyStartYear.toString().length !== 4) {
      return toast.error('Please provide a valid year!');
    }

    const totalYearsInNumber = Number(strategyTotalYears);
    if (isNaN(totalYearsInNumber) || totalYearsInNumber < 1) {
      return toast.error('Total number of years must be atleast 1!');
    }

    setUpdatingStrategyTime(true);

    //set dates of all strategies to whatever is specified.
    //this means we set the initial year of each strategy to specified year
    //and move the start and end date of every instrument relatively
    const updateDatesOfInstrument = (instrument, diffInMonths, key, defaultStartMonthKey, defaultStartYearKey) => {
      const startYearKey = defaultStartYearKey || (key === 'assets' ? 'buyingYear' : 'startYear');
      const startMonthKey = defaultStartMonthKey || (key === 'assets' ? 'buyingMonth' : 'startMonth');
      const endYearKey = key === 'assets' ? 'sellingYear' : 'endYear';
      const endMonthKey = key === 'assets' ? 'sellingMonth' : 'endMonth';

      const { month: newStartMonth, year: newStartYear } = addMonthAndYear(
        instrument[startMonthKey],
        instrument[startYearKey],
        diffInMonths
      );

      instrument[startMonthKey] = newStartMonth;
      instrument[startYearKey] = newStartYear;

      if (instrument[endYearKey]) {
        const { month: newEndMonth, year: newEndYear } = addMonthAndYear(
          instrument[endMonthKey],
          instrument[endYearKey],
          diffInMonths
        );
        instrument[endMonthKey] = newEndMonth;
        instrument[endYearKey] = newEndYear;
      }
    };

    const newStrategies = parentCustomer.strategies.map(strategy => {
      const clonedStrategy = cloneDeep(strategy);
      clonedStrategy.initialYear = yearInNumber;
      clonedStrategy.initialMonth = monthInNumber;
      clonedStrategy.totalYears = totalYearsInNumber;

      const diffInMonths = findDiffInNumberOfMonths(
        strategy.initialMonth,
        strategy.initialYear,
        monthInNumber,
        yearInNumber
      );

      const {
        assets,
        liabilities,
        fixedIncomeAssets,
        oneOffChanges,
        customCashflows,
        investments,
        defaultArmageddonConfig
      } = clonedStrategy;

      [
        { key: 'assets', instruments: assets },
        { key: 'liabilities', instruments: liabilities },
        { key: 'fixedIncomeAssets', instruments: fixedIncomeAssets },
        { key: 'oneOffChanges', instruments: oneOffChanges },
        { key: 'customCashflows', instruments: customCashflows },
        { key: 'investments', instruments: investments },
        { key: 'armageddon', instruments: defaultArmageddonConfig }
      ].forEach(({ instruments, key }) => {
        if (!instruments) return;

        //this is for defaultArmageddonConfig
        if (key === 'armageddon') {
          updateDatesOfInstrument(instruments, diffInMonths, key);
          return;
        }

        instruments.forEach(i => {
          updateDatesOfInstrument(i, diffInMonths, key);

          if (i['cashflowChanges']) {
            i['cashflowChanges'].forEach(c => {
              updateDatesOfInstrument(c, diffInMonths, key);
            });
          }

          if (i['paperApartmentPayments']) {
            i['paperApartmentPayments'].forEach(c => {
              updateDatesOfInstrument(c, diffInMonths, key, 'month', 'year');
            });
          }
        });
      });

      //when we extend strategy i.e. total number of Years is changed
      //1.  liabilities which matures before the expanded date should not expand
      //     but should end at maturity, so we update the end dates of all the liabilities
      //2.  reinvestment dont have null/undefined in there ending dates,
      //    so if reinvestment ends at last month and year of strategy we need to extend it to new last month and year as well

      //1.
      clonedStrategy.liabilities = clonedStrategy.liabilities.map(l => ({
        ...l,
        ...getUpdatedLinkedVariablesOfLiability(
          clonedStrategy,
          clonedStrategy.assets.find(a => a.name === l.relatedAssets?.[0]),
          null,
          l
        )
      }));

      //2.
      const { month: prevLastMonth, year: prevLastYear } = addMonthAndYear(
        monthInNumber,
        yearInNumber,
        -1, //last month is just one month before
        strategy.totalYears
      );
      const { month: newLastMonth, year: newLastYear } = addMonthAndYear(
        monthInNumber,
        yearInNumber,
        -1,
        totalYearsInNumber
      );

      clonedStrategy.investments = clonedStrategy.investments.map(i =>
        areMonthYearEqual(i.endMonth, i.endYear, prevLastMonth, prevLastYear)
          ? { ...i, endMonth: newLastMonth, endYear: newLastYear }
          : i
      );

      return clonedStrategy;
    });

    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.USERS_UPDATE,
      requestBody: {
        strategies: newStrategies,
        _id: activeCustomer['_id']
      }
    });

    setUpdatingStrategyTime(false);

    if (error) {
      return toast.error(error);
    }

    onCustomerUpdate(response);
    setUpdateStrategyTimeMeta(null);
    toast.success(`Successfully updated strategies!`);
  };

  const onViewFilesClick = () => {
    setShowFiles(true);
  };

  const onViewAppointmentClick = () => {
    setViewAppointmentModalMeta(parentCustomer);
  };

  const onFileDeleteClick = file => {
    setDeleteFileModalMeta({ file });
  };

  const deleteFile = async () => {
    const { file: toBeDeletedFile } = deleteFileModalMeta;
    setDeleteFileModalMeta({ ...deleteFileModalMeta, deletingFile: true });

    const newFileArray = activeCustomer.files.filter(f => f._id !== toBeDeletedFile._id).map(f => f._id);

    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.USERS_UPDATE,
      requestBody: {
        files: newFileArray,
        _id: activeCustomer['_id']
      }
    });

    setDeleteFileModalMeta({ ...deleteFileModalMeta, deletingFile: false });
    if (error) {
      return toast.error(error);
    }

    setDeleteFileModalMeta(null);
    onCustomerUpdate(response);
    deleteFileFromServer(toBeDeletedFile);
  };

  const onUploadFileChange = async (uploadedFiles = []) => {
    if (!uploadedFiles.length || uploadedFiles.some(f => ['WAITING_FOR_UPLOAD', 'UPLOADING'].includes(f.status)))
      return;

    let filesToUpload = uploadedFiles.filter(f => f.status !== 'ERROR');

    setFileUploadingToCustomer(true);

    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.USERS_UPDATE,
      requestBody: {
        files: [...activeCustomer.files.map(f => f._id), ...filesToUpload.map(f => f.id)],
        _id: activeCustomer['_id']
      }
    });

    setFileUploadingToCustomer(false);

    if (error) {
      return toast.error(error);
    }

    onCustomerUpdate(response);

    if (uploadedFiles.length !== filesToUpload.length)
      return toast.error(
        'Some of the files were not uploaded due to some error. Please try uploading the files again!'
      );
  };

  const onShowAllStrategiesClick = async () => {
    setCustomerAddingUpdating(true);

    const { customer } = customerActionMeta;

    const showToCustomer = !customer.strategies.every(s => s.showToCustomer);

    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.USERS_UPDATE,
      requestBody: {
        strategies: customer.strategies.map(s => ({ ...s, showToCustomer })),
        _id: customer['_id']
      }
    });

    setCustomerAddingUpdating(false);

    if (error) {
      return toast.error(error);
    }

    onCustomerUpdate(response);
    setCustomerActionMeta(null);
    toast.success(`Successfully updated strategies!`);
  };

  const makePhoneCall = async () => {
    const { phone, crmContact } = activeCustomer;
    const phoneNumber = phone || crmContact?.phoneNumbers[0] || '';
    if (!phoneNumber) {
      return toast.info('No phone number available');
    }

    const { response, error } = await makeApiRequests({
      requestBody: {
        phoneNumber
      },
      endpoint: ENDPOINTS.DIAL_PHONE_NUMBER,
      method: 'POST'
    });

    if (error) {
      toast.error(error);
      return;
    }
  };

  const onInviteCustomerSubmit = async () => {
    const { receiverEmails, customer } = inviteCustomerModalMeta;

    // Checking if userId and password is not blank or has atleast 6 characters, there should be atleast one receiver email

    if (!receiverEmails || receiverEmails.length === 0) {
      return toast.error('There must be at least one receiver email.');
    }

    setInviteCustomerModalMeta({ ...inviteCustomerModalMeta, showProgress: true });

    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.FORGOT_PASSWORD,
      requestBody: { _id: customer._id, receiverEmails, email: customer?.email }
    });

    setInviteCustomerModalMeta({ ...inviteCustomerModalMeta, showProgress: false });
    if (error) {
      return toast.error(error);
    }

    toast.success('Password reset link was successfully sent');
    setInviteCustomerModalMeta(null);
    onCustomerUpdate(response);
  };

  const onSendInvitationLinkToCustomerSubmit = async () => {
    const { receiverEmails, customer } = inviteCustomerModalMeta;
    if (!receiverEmails || receiverEmails.length === 0) {
      return toast.error('There must be at least one receiver email.');
    }

    setInviteCustomerModalMeta({ ...inviteCustomerModalMeta, showProgress: true });

    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.SEND_INVITATION_LINK,
      requestBody: { userId: customer._id, receiverEmails, email: customer?.email }
    });

    setInviteCustomerModalMeta({ ...inviteCustomerModalMeta, showProgress: false });
    if (error) {
      return toast.error(error);
    }

    toast.success('Invitation link was successfully sent');
    setInviteCustomerModalMeta(null);
    onCustomerUpdate(response);
  };

  const onLogsClick = async () => {
    setShowLogsModal(true);
  };

  const onUndoClick = async () => {
    undoRedo('undo');
  };
  const onRedoClick = async () => {
    undoRedo('redo');
  };
  const onUndoRedoClick = async (action, diffId) => {
    undoRedo(action, diffId);
  };

  const undoRedo = async (action, diffId) => {
    setCustomerUpdating(true);

    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.USERS_DIFF_ACTION(parentCustomer._id, action),
      requestBody: { id: diffId }
    });

    setCustomerUpdating(false);
    if (error) {
      return toast.error(error);
    }

    toast.success(`${action.toUpperCase()} done successfully!`);
    setInviteCustomerModalMeta(null);
    onCustomerUpdate(response);
  };

  const onViewCRMContactClick = async contact => {
    if (contact) {
      if (contact?._id) {
        window.open(`${process.env.REACT_APP_CRM_APP_LINK}/contacts?contact=${contact._id}&view=contact`, '_blank');
      }
    }
  };

  const customerEmailsToSearch = useMemo(() => customerEmailsFromUrl?.split(','), [customerEmailsFromUrl]);

  useEffect(() => {
    if (!initialCustomers?.length && !customerEmailsToSearch?.length && !customerIdFromUrl) return;

    const customerList = cloneDeep(initialCustomers);
    const foundCustomer = customerList.find(customer =>
      customerEmailsToSearch?.length
        ? customerEmailsToSearch.some(email => email?.toLowerCase()?.trim() === customer?.email?.toLowerCase()?.trim())
        : customer._id === customerIdFromUrl
    );

    if (!foundCustomer) {
      // toast.error('Customer not found');
      return;
    }

    const index = customerList.findIndex(c => c._id === foundCustomer._id);

    if (index !== -1) {
      const [customer] = customerList.splice(index, 1);
      customerList.unshift(customer);
    }

    setCustomers(customerList);
    setParentCustomer(foundCustomer);
  }, [initialCustomers, customerEmailsToSearch, customerIdFromUrl]);

  return (
    <>
      <Container fluid className={'h-100 py-2 px-md-3 bg-[#F5F5F5]'}>
        {loading ? (
          <Loader />
        ) : customers.length > 0 ? (
          <>
            <Row className="h-100 py-2 mx-0">
              {screenWidth >= 768 && (
                <Col
                  xs={0}
                  md={isSidebarCollapsed ? 0 : 2}
                  lg={isSidebarCollapsed ? 0 : 2}
                  xl={isSidebarCollapsed ? 0 : 2}
                  className="p-0 pb-5 overflow-auto h-100 overlapping-list d-none d-md-block"
                >
                  <Card>
                    <Card.Body className="px-1 py-2">
                      <CustomerList
                        selectedStatus={selectedStatus}
                        setSelectedStatus={setSelectedStatus}
                        customers={displayedCustomers}
                        userList={userList}
                        selectedUsers={selectedUsers}
                        onUserFilterChange={handleUserFilterChange}
                        showUserFilter={isAdmin(user?.role)}
                        loadingFilteredList={
                          selectedUsers != null && (deletedCustomersLoading || customersModifiedByUserLoading)
                        }
                        activeId={parentCustomer ? parentCustomer['_id'] : ''}
                        onSelect={customer => {
                          delete customer.loadingError;
                          setActiveCustomer(customer);
                          setParentCustomer(customer);
                        }}
                        onHoverInactive={() => {
                          setSidebarCollapsed(true);
                        }}
                        hoverDelay={5000}
                      />
                    </Card.Body>
                  </Card>
                </Col>
              )}
              <Col
                xs={12}
                md={isSidebarCollapsed ? 12 : 10}
                lg={isSidebarCollapsed ? 12 : 10}
                xl={isSidebarCollapsed ? 12 : 10}
                className="px-1"
              >
                {parentCustomer && activeCustomer && (
                  <>
                    <NewCustomerCard
                      onViewCRMContactClick={onViewCRMContactClick}
                      adminCompTable={adminCompTable}
                      parentCustomer={parentCustomer}
                      activeCustomer={activeCustomer}
                      onActiveCustomerSwitched={onActiveCustomerSwitched}
                      onCompTableClick={() => setShowCompTable(true)}
                      onCustomerGeneralInfoClick={() => setShowCustomerClientInfo(true)}
                      onEditClick={() =>
                        setCustomerActionMeta({
                          customer: parentCustomer,
                          mode: 'edit',
                          strategyStartYear: parentCustomer['strategies'][0]?.initialYear || new Date().getFullYear(),
                          strategyStartMonth: parentCustomer['strategies'][0]?.initialMonth || 0,
                          strategyTotalYears: parentCustomer['strategies'][0]?.totalYears || 10
                        })
                      }
                      onUpdateStrategyTimeClick={() =>
                        setUpdateStrategyTimeMeta({
                          customer: parentCustomer,
                          mode: 'edit',
                          strategyStartYear: parentCustomer['strategies'][0]?.initialYear || new Date().getFullYear(),
                          strategyStartMonth: parentCustomer['strategies'][0]?.initialMonth || 0,
                          strategyTotalYears: parentCustomer['strategies'][0]?.totalYears || 10
                        })
                      }
                      onDeleteClick={isAdmin(role) ? onDeleteCustomerClick : undefined}
                      onArchiveClick={onArchiveCustomerClick}
                      onCompareStrategyClick={
                        activeCustomer['strategies']?.filter(s => !s.killed).length > 0 && onCompareStrategyClick
                      }
                      onLogsClick={onLogsClick}
                      onUndoClick={onUndoClick}
                      onRedoClick={onRedoClick}
                      onCollapseIconClick={alterExpandCollapse}
                      isSidebarCollapsed={isSidebarCollapsed}
                      onCustomerUpdate={onCustomerUpdate}
                      onReapplyCompClick={onReapplyCompClick}
                      onSelectedStrategyChange={setActiveCustomerStrategy}
                      onShareClick={onShareClick}
                      onViewFilesClick={onViewFilesClick}
                      onViewAppointmentClick={onViewAppointmentClick}
                      onUploadFileChange={onUploadFileChange}
                      isFileUploadingToCustomer={isFileUploadingToCustomer}
                      customerUpdating={customerUpdating || isFileUploadingToCustomer}
                      showCollapseIcon
                      makePhoneCall={makePhoneCall}
                      onAddProfileClick={data => handleAddProfileClick(data, activeCustomer)}
                      assigneUsers={assigneUsers}
                      selectedUsers={selectedUsers}
                    />
                    <FileViewer
                      show={showFiles}
                      onHide={() => setShowFiles(false)}
                      customerForThisDriveFolder={activeCustomer}
                      onCustomerUpdate={onCustomerUpdate}
                      driveFolder={activeCustomer.driveFolder}
                      files={activeCustomer.files}
                      onFileDeleteClick={onFileDeleteClick}
                    />

                    <AlertModal
                      show={deleteFileModalMeta !== null}
                      alertText={translate('are_you_sure_delete_file', {
                        fileName:
                          deleteFileModalMeta?.file?.metadata?.originalName || deleteFileModalMeta?.file?.filename || ''
                      })}
                      showProgress={deleteFileModalMeta && deleteFileModalMeta.deletingFile}
                      progressText={`${translate('deleting_file')}...`}
                      continueButtonText={translate('delete')}
                      onDismissClick={() => setDeleteFileModalMeta(null)}
                      onHide={() => setDeleteFileModalMeta(null)}
                      onContinueClick={deleteFile}
                    />
                    <CustomerShareDialog
                      customer={activeCustomer}
                      show={shareDialogMeta}
                      onHide={() => setShareDialogMeta(null)}
                      onUserRemoveClick={onShareUserRemoveClick}
                      userBeingAdded={shareDialogMeta?.userBeingAdded}
                      onUserBeingAddedChange={email =>
                        setShareDialogMeta({ ...shareDialogMeta, userBeingAdded: email })
                      }
                      onAddUserClick={onShareUserAddClick}
                      showProgress={shareDialogMeta?.showProgress}
                      translate={translate}
                    />
                    <CompTableSidebar
                      customer={activeCustomer}
                      show={showCompTable}
                      onHide={() => setShowCompTable(false)}
                      onCompUpdate={onCompUpdate}
                      translate={translate}
                    />

                    <CustomerClientInfoDialog
                      customer={parentCustomer}
                      show={showCustomerClientInfo}
                      onHide={() => setShowCustomerClientInfo(false)}
                      onCustomerUpdate={onCustomerUpdate}
                      translate={translate}
                      editable={!readOnlyMode}
                    />
                    {activeCustomer.strategies?.length > 0 && (
                      <ReapplyCompModal
                        show={reapplyCompModalMeta !== null}
                        presetFields={reapplyCompModalMeta?.presetFields}
                        strategies={reapplyCompModalMeta?.strategies}
                        selectedStrategies={reapplyCompModalMeta?.selectedStrategies}
                        selectedFields={reapplyCompModalMeta?.selectedFields}
                        onStrategyCheckChange={onReapplyCompStrategyCheckChange}
                        onFieldCheckChange={onReapplyCompFieldCheckChange}
                        onHide={() => setReapplyCompModalMeta(null)}
                        onStrategyAllCheck={onStrategyAllCheck}
                        onFieldsAllCheck={onFieldsAllCheck}
                        onApplyClick={reapplyComp}
                        showProgress={reapplyCompModalMeta?.showProgress}
                      />
                    )}

                    {activeCustomerStrategy && (
                      <ComparisonChartSidebar
                        customer={activeCustomer}
                        adminCompTable={adminCompTable}
                        show={showComparisonChart}
                        activeStrategy={activeCustomerStrategy}
                        onHide={() => setShowComparisonChart(false)}
                        translate={translate}
                      />
                    )}
                  </>
                )}
              </Col>
            </Row>
            <ViewAppointmentModal
              show={Boolean(viewAppointmentModalMeta)}
              onHide={() => setViewAppointmentModalMeta(null)}
              customer={parentCustomer}
            />
            {screenWidth < 768 && (
              <SlidingSideBar
                title={translate('customers')}
                onClose={() => setSidebarCollapsed(true)}
                placement="left"
                className="d-md-none"
                visible={!isSidebarCollapsed}
                smallScreenWidth={80}
              >
                <Card>
                  <Card.Body className="px-1 py-2">
                    <CustomerList
                      selectedStatus={selectedStatus}
                      setSelectedStatus={setSelectedStatus}
                      customers={displayedCustomers}
                      userList={userList}
                      selectedUsers={selectedUsers}
                      onUserFilterChange={handleUserFilterChange}
                      showUserFilter={isAdmin(user?.role)}
                      loadingFilteredList={selectedUsers != null && deletedCustomersLoading}
                      activeId={parentCustomer ? parentCustomer['_id'] : ''}
                      onSelect={customer => {
                        delete customer.loadingError;

                        setActiveCustomer(customer);
                        setParentCustomer(customer);
                        setSidebarCollapsed(true);
                      }}
                      onHoverInactive={() => {
                        setSidebarCollapsed(true);
                      }}
                      hoverDelay={5000}
                    />
                  </Card.Body>
                </Card>
              </SlidingSideBar>
            )}
          </>
        ) : (
          <NotFound text="No customers added!" />
        )}
        {customers.length > 0 && (
          <AlertModal
            alertText={showAlertMeta ? showAlertMeta.alertText : ''}
            show={showAlertMeta !== null}
            onHide={() => setShowAlertMeta(null)}
            continueButtonVariant={showAlertMeta ? showAlertMeta.continueButtonVariant : ''}
            onDismissClick={() => setShowAlertMeta(null)}
            onContinueClick={showAlertMeta ? showAlertMeta.onContinueClick : ''}
            showProgress={showAlertMeta && showAlertMeta.showProgress}
            progressText={showAlertMeta ? showAlertMeta.progressText : ''}
          />
        )}
        <UpdateStrategyTimeModal
          translate={translate}
          show={updateStrategyTimeMeta !== null}
          onHide={() => setUpdateStrategyTimeMeta(null)}
          strategyStartMonth={updateStrategyTimeMeta?.strategyStartMonth}
          strategyStartYear={updateStrategyTimeMeta?.strategyStartYear}
          onStartMonthChange={e =>
            setUpdateStrategyTimeMeta({ ...updateStrategyTimeMeta, strategyStartMonth: e.target.value })
          }
          onStartYearChange={e =>
            setUpdateStrategyTimeMeta({ ...updateStrategyTimeMeta, strategyStartYear: e.target.value })
          }
          strategyTotalYears={updateStrategyTimeMeta?.strategyTotalYears}
          onTotalYearsChange={e =>
            setUpdateStrategyTimeMeta({ ...updateStrategyTimeMeta, strategyTotalYears: e.target.value })
          }
          onStrategyStartDateUpdateSubmit={onStrategyStartDateUpdateSubmit}
          showProgress={updatingStrategyTime}
        />
        <AddEditCustomerModal
          show={customerActionMeta !== null}
          onHide={() => setCustomerActionMeta(null)}
          onSendInvitationLinkToCustomerClick={onSendInvitationLinkToCustomerClick}
          onInviteClick={onInviteCustomerClick}
          mode={customerActionMeta?.mode || 'add'}
          onCloneClick={() => {
            setCustomerActionMeta({
              toBeClonedCustomer: parentCustomer,
              mode: 'clone',
              customer: {
                ...getCustomerForClone(parentCustomer),
                startDate: getFormattedDate(new Date()),
                sendEmail: true
              }
            });
          }}
          onShowAllStrategiesClick={onShowAllStrategiesClick}
          customer={customerActionMeta?.customer}
          showProgress={customerAddingUpdating}
          user={user}
          users={assigneUsers}
          onChange={onCustomerDetailChange}
          onViewCRMContactClick={onViewCRMContactClick}
          onCustomerSubmit={onCustomerSubmit}
          setParentCustomer={setParentCustomer}
          parentCustomer={parentCustomer}
        />

        <InviteCustomerModal
          show={inviteCustomerModalMeta !== null}
          hasAlreadyBeenInvited={inviteCustomerModalMeta?.hasAlreadyBeenInvited}
          password={inviteCustomerModalMeta?.password}
          onPasswordChange={password => setInviteCustomerModalMeta({ ...inviteCustomerModalMeta, password })}
          receiverEmails={inviteCustomerModalMeta?.receiverEmails}
          onReceiverEmailsChange={receiverEmails =>
            setInviteCustomerModalMeta({ ...inviteCustomerModalMeta, receiverEmails })
          }
          onHide={() => setInviteCustomerModalMeta(null)}
          showProgress={inviteCustomerModalMeta?.showProgress}
          onSubmit={
            inviteCustomerModalMeta?.isInvitationLink ? onSendInvitationLinkToCustomerSubmit : onInviteCustomerSubmit
          }
          headingLabel={inviteCustomerModalMeta?.headingLabel}
        />
        <UserLogsModal
          disabled={customerUpdating}
          onUndoRedoClick={onUndoRedoClick}
          show={showLogsModal}
          user={parentCustomer}
          onHide={() => setShowLogsModal(false)}
        />
        <AddEditCustomerProfileModal
          show={Boolean(customerProfileMeta)}
          onHide={closeProfileModal}
          showProgress={customerProfileMeta?.showProgress}
          editMode={customerProfileMeta?.mode === 'update'}
          initialValues={customerProfileMeta?.data}
          onSubmit={profile => submitCustomerProfile(profile, activeCustomer, onCustomerUpdate)}
          onDeleteClick={handleDeleteProfileClick}
          onToggleApproval={(profile, isApproved) =>
            toggleProfileApproval(profile, isApproved, activeCustomer, onCustomerUpdate)
          }
          onPublishForReview={profile => publishProfileForReview(profile, activeCustomer, onCustomerUpdate)}
          relatedDeals={activeCustomer?.deals}
          fromCustomerPortal={false}
        />
        <AlertModal
          show={Boolean(deleteApartmentProfileMeta)}
          showProgress={deleteApartmentProfileMeta?.showProgress}
          onHide={closeDeleteModal}
          onDismissClick={closeDeleteModal}
          onContinueClick={() => deleteApartmentProfile(activeCustomer, onCustomerUpdate)}
          alertText={translate('are_you_sure_you_want_to_delete_apartment_profile')}
        />
      </Container>
    </>
  );
};

export default Customer;
